import { useMemo, useState } from "react";
import { latestUpdates, updateCategories } from "@/mocks/homeData";

export default function LatestUpdates() {
  const [activeCategory, setActiveCategory] = useState("全部");
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    return latestUpdates.filter((item) => {
      const matchCategory = activeCategory === "全部" || item.category === activeCategory;
      const matchKeyword =
        kw.length === 0 ||
        item.title.includes(kw) ||
        item.summary.includes(kw) ||
        item.category.includes(kw);
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, keyword]);

  return (
    <section id="updates" className="w-full border-b border-background-200 bg-background-50">
      <div className="mx-auto w-full max-w-shell px-4 py-14 md:px-6 md:py-20 lg:px-8">
        <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="flex items-center gap-2 font-label text-xs tracking-[0.28em] text-secondary-600">
              <i className="ri-history-line" />
              最新更新
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              实时滚动的新料时间线
            </h2>
            <p className="mt-3 max-w-xl font-body text-sm leading-relaxed text-foreground-600">
              按时间先后排列，每一条都配一句短摘要，扫一眼就知道值不值得点进去。
            </p>
          </div>

          <label className="flex w-full items-center gap-3 rounded-md border border-background-300 bg-background-100 px-4 py-3 lg:w-80">
            <i className="ri-search-line text-lg text-foreground-500" />
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜关键词，比如：开机、口碑"
              className="w-full bg-transparent font-body text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            {keyword.length > 0 && (
              <button
                type="button"
                aria-label="清空搜索"
                onClick={() => setKeyword("")}
                className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-foreground-500 hover:text-primary-600"
              >
                <i className="ri-close-line" />
              </button>
            )}
          </label>
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-2">
          {updateCategories.map((category) => {
            const isActive = category === activeCategory;
            return (
              <button
                key={category}
                type="button"
                onClick={() => setActiveCategory(category)}
                className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-1.5 font-label text-sm transition-colors ${
                  isActive
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {category}
              </button>
            );
          })}
          <span className="ml-auto font-label text-xs text-foreground-500">
            当前显示 {filtered.length} 条
          </span>
        </div>

        <div className="flex flex-col divide-y divide-background-200 border-y border-background-200">
          {filtered.map((item) => (
            <article
              key={item.id}
              className="group flex flex-col gap-3 py-5 md:flex-row md:items-start md:gap-6"
            >
              <div className="flex shrink-0 items-center gap-3 md:w-32 md:flex-col md:items-start md:gap-1">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-700 md:h-8 md:w-8">
                  <i className="ri-bookmark-3-line" />
                </span>
                <span className="font-label text-xs text-foreground-500 md:mt-1">{item.time}</span>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-background-100 px-2 py-0.5 font-label text-[11px] text-foreground-600">
                    {item.category}
                  </span>
                  {item.isNew && (
                    <span className="rounded bg-accent-500 px-2 py-0.5 font-label text-[11px] text-foreground-950">
                      刚更新
                    </span>
                  )}
                </div>
                <h3 className="mt-2 font-heading text-lg font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                  <a href="#top" className="cursor-pointer">
                    {item.title}
                  </a>
                </h3>
                <p className="mt-2 max-w-3xl font-body text-sm leading-relaxed text-foreground-600">
                  {item.summary}
                </p>
              </div>

              <span className="hidden shrink-0 items-center gap-1 self-center font-label text-xs text-foreground-500 transition-colors group-hover:text-primary-600 md:flex">
                看详情
                <i className="ri-arrow-right-line" />
              </span>
            </article>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-inbox-line" />
            </span>
            <p className="font-body text-sm text-foreground-600">
              没找到「{keyword}」相关更新，换个关键词试试。
            </p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setActiveCategory("全部");
              }}
              className="cursor-pointer rounded-md border border-background-300 px-4 py-2 font-label text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-600"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}